import type React from "react";

export type ConfirmationVariant = "error" | "info";
export type ConfirmActionColor = "primary" | "danger" | "secondary";

export type ConfirmAction<T = string> = {
    id: T;
    label: string;
    color?: ConfirmActionColor;
};

export type ConfirmOptions<T = string> = {
    title: string;
    /** Plain text or formatted content, e.g. to list affected items */
    message: string | React.ReactNode;
    /**
     * Shown left to right; the last action is rendered as the primary button.
     */
    actions: ConfirmAction<T>[];
    variant?: ConfirmationVariant;
};

class ConfirmationServiceImpl<T = string> {
    private _resolver: ((result: T) => void) | null = null;
    private _showDialogCallback: ((options: ConfirmOptions<T>) => void) | null = null;

    setShowDialogCallback(callback: (options: ConfirmOptions<T>) => void) {
        this._showDialogCallback = callback;
    }

    confirm(options: ConfirmOptions<T>): Promise<T> {
        if (!this._showDialogCallback) {
            throw new Error("ConfirmationService: Show dialog callback is not set.");
        }

        return new Promise<T>((resolve) => {
            this._resolver = resolve;
            this._showDialogCallback!(options);
        });
    }

    resolve(result: T): void {
        this._resolver?.(result);
        this._resolver = null; // Reset resolver after resolving
    }
}

// Making a singleton instance of ConfirmationService
export const ConfirmationService = new ConfirmationServiceImpl();
